import * as React from 'react';

/**
 * Available feature flags.
 *
 * Add new beta flags to this union (and to {@link FeatureFlagState} +
 * {@link getDefaultFlags}); each one becomes a Settings toggle and a
 * ``window.antioch.featureFlags`` console command for free.
 */
export type FeatureFlag = 'viewportAnnotations';

/**
 * Feature flag state interface.
 */
interface FeatureFlagState {
  viewportAnnotations: boolean;
}

interface FeatureFlagContextValue {
  flags: FeatureFlagState;
  isEnabled: (flag: FeatureFlag) => boolean;
  setFlag: (flag: FeatureFlag, enabled: boolean) => void;
}

const FeatureFlagContext = React.createContext<FeatureFlagContextValue | undefined>(undefined);

const STORAGE_KEY = 'antioch-feature-flags';

/**
 * Get stored feature flags from localStorage.
 */
function getStoredFlags(): Partial<FeatureFlagState> {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as Partial<FeatureFlagState>;
  } catch {
    // Invalid JSON, return empty
  }

  return {};
}

/**
 * Save feature flags to localStorage.
 */
function saveFlags(flags: Partial<FeatureFlagState>): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // Storage full or unavailable
  }
}

/**
 * Get default flag values.
 * All flags default to false unless explicitly enabled.
 */
function getDefaultFlags(): FeatureFlagState {
  return {
    viewportAnnotations: false,
  };
}

/**
 * Merge stored flags with defaults.
 */
function initializeFlags(): FeatureFlagState {
  const defaults = getDefaultFlags();
  const stored = getStoredFlags();

  return {
    ...defaults,
    ...stored,
  };
}

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlagsState] = React.useState<FeatureFlagState>(initializeFlags);

  const isEnabled = React.useCallback(
    (flag: FeatureFlag): boolean => {
      return flags[flag] ?? false;
    },
    [flags]
  );

  const setFlag = React.useCallback((flag: FeatureFlag, enabled: boolean) => {
    setFlagsState((prev) => {
      const next = { ...prev, [flag]: enabled };
      saveFlags(next);
      return next;
    });
  }, []);

  // Expose a console API for developers to flip flags without the Settings UI.
  React.useEffect(() => {
    const api = {
      list: () => {
        if (import.meta.env.DEV) {
          console.table(flags);
        }
        return flags;
      },
      get: (flag: FeatureFlag) => {
        const value = flags[flag];
        if (import.meta.env.DEV) {
          console.log(`Feature flag "${flag}": ${value}`);
        }
        return value;
      },
      enable: (flag: FeatureFlag) => {
        setFlag(flag, true);
        if (import.meta.env.DEV) {
          console.log(`Feature flag "${flag}" enabled`);
        }
      },
      disable: (flag: FeatureFlag) => {
        setFlag(flag, false);
        if (import.meta.env.DEV) {
          console.log(`Feature flag "${flag}" disabled`);
        }
      },
      reset: () => {
        const defaults = getDefaultFlags();
        setFlagsState(defaults);
        saveFlags({});
        if (import.meta.env.DEV) {
          console.log('All feature flags reset to defaults');
          console.table(defaults);
        }
      },
      help: () => {
        if (import.meta.env.DEV) {
          console.log(`
Antioch Feature Flags API
=========================

Available commands:
  antioch.featureFlags.list()         - List all feature flags and their values
  antioch.featureFlags.get(flag)      - Get the value of a specific flag
  antioch.featureFlags.enable(flag)   - Enable a flag
  antioch.featureFlags.disable(flag)  - Disable a flag
  antioch.featureFlags.reset()        - Reset all flags to defaults

Available flags:
  viewportAnnotations  - Enable browser-drawn viewport annotations (beta)

Examples:
  antioch.featureFlags.enable('viewportAnnotations')
  antioch.featureFlags.list()
        `);
        }
      },
    };

    (window as unknown as { antioch: { featureFlags: typeof api } }).antioch = {
      ...(window as unknown as { antioch?: Record<string, unknown> }).antioch,
      featureFlags: api,
    };

    if (import.meta.env.DEV) {
      console.log(
        '%c🚩 Feature Flags API available: antioch.featureFlags.help()',
        'color: #7c3aed; font-weight: bold;'
      );
    }

    return () => {
      const win = window as unknown as { antioch?: { featureFlags?: typeof api } };
      if (win.antioch?.featureFlags) {
        delete win.antioch.featureFlags;
      }
    };
  }, [flags, setFlag]);

  const value = React.useMemo(() => ({ flags, isEnabled, setFlag }), [flags, isEnabled, setFlag]);

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags() {
  const context = React.useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}
